/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { ExclamationCircleIcon } from '@heroicons/react/16/solid';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

const input = cva(
  [
    'grow',
    'block',
    'w-full',
    'rounded-md',
    'bg-white',
    'py-1.5',
    'pr-10',
    'pl-3',
    'text-base',
    'border-gray-200',
    'sm:text-sm/6',
    'sm:pr-9',
    'sm:text-sm',
  ],
  {
    variants: {
      intent: {
        primary: [
          'text-base',
          'text-gray-950',
          'outline-1',
          '-outline-offset-1',
          'outline-gray-200',
          'placeholder:text-gray-400',
          'focus:outline-2',
          'focus:-outline-offset-2',
          'focus:outline-blue-200',
        ],
        invalid: [
          'text-red-900',
          'border-red-300',
          'outline-1',
          '-outline-offset-1',
          'outline-red-300',
          'placeholder:text-red-300',
          'focus:outline-2',
          'focus:-outline-offset-2',
          'focus:outline-red-600',
        ],
      },
      size: {
        xs: ['text-xs/4', 'px-2'],
        sm: ['px-3', 'py-1.5', 'h-8'],
        lg: [],
      },
      disabled: {
        true: ['cursor-not-allowed', 'bg-gray-100', 'text-gray-400'],
        false: [],
      },
    },
    defaultVariants: {},
  }
);

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  name: string;
  type?: string;
  placeholder?: string;
  value?: string;
  isInvalid?: boolean;
  validationMessage?: string;
  helperText?: string;
  sizeVariant?: 'xs' | 'sm' | 'lg';
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  optional?: boolean;
  orientation?: 'vertical' | 'horizontal';
  grid?: boolean;
}

export function Input({
  label = undefined,
  name,
  type = 'text',
  placeholder = '',
  value,
  isInvalid = false,
  validationMessage = '',
  helperText = '',
  sizeVariant = 'sm',
  onChange,
  optional,
  orientation = 'vertical',
  grid = false,
  ...props
}: InputProps) {
  const isHorizontal = orientation === 'horizontal';
  const isNumeric = type === 'number';
  const numericGuards = isNumeric
    ? {
        min: props.min ?? 0,
        step: props.step ?? 1,
        onWheel:
          props.onWheel ??
          ((event: React.WheelEvent<HTMLInputElement>) =>
            event.currentTarget.blur()),
      }
    : {};
  const valueThatKeepsTypedDecimals =
    isNumeric && value ? Number(value) : value;

  return (
    <div
      className={twMerge(
        'grow',
        isHorizontal && grid
          ? 'grid grid-cols-[1fr_auto] items-center gap-2 w-full'
          : isHorizontal
          ? 'flex items-center space-x-4'
          : 'flex flex-col'
      )}
    >
      {label && (
        <div className={twMerge(
          isHorizontal ? 'flex flex-col shrink-0' : 'flex items-center',
          isHorizontal ? 'min-w-[10rem]' : ''
        )}>
          <label
            htmlFor={name}
            className='block text-sm leading-5 font-medium text-gray-900 font-inter'
          >
            {label}
          </label>
          {optional && (
            <span
              className={` text-sm leading-5 font-normal font-inter text-gray-400 ${
                isHorizontal ? '' : 'ml-2'
              }`}
            >
              Optional
            </span>
          )}
        </div>
      )}
      <div
        className={twMerge(
          'relative',
          label && !isHorizontal ? 'mt-2' : '',
          isHorizontal && !grid ? 'flex-1 flex flex-col' : '',
          isHorizontal && grid ? 'w-48' : ''
        )}
      >
        <div className='relative'>
          <input
            id={name}
            name={name}
            type={type}
            placeholder={placeholder}
            value={valueThatKeepsTypedDecimals}
            aria-invalid={isInvalid}
            aria-describedby={isInvalid ? `${name}-error` : undefined}
            onChange={onChange}
            className={twMerge(
              input({
                intent: isInvalid ? 'invalid' : 'primary',
                size: sizeVariant,
                disabled: props.disabled,
              }),
              'pr-10'
            )}
            {...props}
            {...numericGuards}
          />
          {isInvalid && (
            <span
              className={twMerge(
                'pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3'
              )}
            >
              <ExclamationCircleIcon
                aria-hidden='true'
                className='h-5 w-5 text-red-500'
              />
            </span>
          )}
        </div>
        {isInvalid && validationMessage && (
          <p id={`${name}-error`} className='mt-2 text-sm text-red-600'>
            {validationMessage}
          </p>
        )}
        {helperText && (
          <p className='mt-2 text-sm text-gray-500'>{helperText}</p>
        )}
      </div>
    </div>
  );
}
